import React, { useState } from "react";
import {
  Checkbox,
  FormControlLabel,
  FormGroup,
  TextField,
  Menu,
  MenuItem,
  IconButton,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import AddIcon from "@mui/icons-material/Add";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { CustomJobPhase } from "hooks/jobs/job-phases/useGetJobPhase";
import useUpdateJobPhaseChecklistItems from "hooks/jobs/job-phases/useUpdateJobPhaseChecklistItems";
import useGetOrganizationProposalChecklistItems from "hooks/organization/useGetOrganizationProposalChecklistItems";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import CreateChecklistItemModal from "../../proposals/CreateChecklistItemModal";

interface ChecklistItem {
  id?: string;
  checklistItemId: string;
  name: string;
  isCompleted: boolean;
}

interface FormInputs {
  checklistItems: ChecklistItem[];
  checklistNotes: string;
}

const JobPhaseChecklist: React.FC<{ jobPhase: CustomJobPhase }> = ({ jobPhase }) => {
  const [updateChecklistItems] = useUpdateJobPhaseChecklistItems();
  const { data: organizationChecklistItems, loading: loadingChecklistItems } =
    useGetOrganizationProposalChecklistItems();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { isDirty, isSubmitting },
    reset,
  } = useForm<FormInputs>({
    defaultValues: {
      checklistItems: jobPhase.checklistItems.map((item) => ({
        id: item.id,
        checklistItemId: item.checklistItem.id,
        name: item.checklistItem.name,
        isCompleted: item.isCompleted,
      })),
      checklistNotes: jobPhase.checklistNotes || "",
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "checklistItems",
  });

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleAddChecklistItem = (item: { id: string; name: string }) => {
    if (!fields.some((field) => field.checklistItemId === item.id)) {
      append({
        checklistItemId: item.id,
        name: item.name,
        isCompleted: false,
      });
    }
    handleClose();
  };

  const handleCreateNewItem = () => {
    setCreateModalOpen(true);
    handleClose();
  };

  const handleCreateModalClose = () => {
    setCreateModalOpen(false);
  };

  const handleCreateSuccess = (newItem: { id: string; name: string }) => {
    if (!fields.some((field) => field.checklistItemId === newItem.id)) {
      append({
        checklistItemId: newItem.id,
        name: newItem.name,
        isCompleted: false,
      });
    }
  };

  const onSubmit = async (data: FormInputs) => {
    const input = {
      id: jobPhase.id,
      checklistNotes: data.checklistNotes,
      checklistItems: {
        upsert: data.checklistItems.map((item) => ({
          jobId: jobPhase.job.id,
          ...(item.id && { id: item.id }),
          isCompleted: item.isCompleted,
          checklistItemId: item.checklistItemId,
        })),
      },
    };

    updateChecklistItems({
      variables: {
        input,
      },
    });

    reset({}, { keepValues: true });
  };

  return (
    <>
      <MDBox p={2}>
        <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <MDTypography variant="h6" fontWeight="medium">
            Checklist Items
          </MDTypography>
          <MDButton
            className="tour-checklist-select"
            color="info"
            size="small"
            onClick={handleClick}
            disabled={loadingChecklistItems}
            variant="outlined"
          >
            Select a checklist item
          </MDButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
          >
            <MenuItem
              onClick={handleCreateNewItem}
              sx={{
                backgroundColor: "action.hover",
                borderRadius: 1,
                fontWeight: "medium",
                mx: 1,
                mb: 1,
                "&:hover": {
                  backgroundColor: "action.selected",
                },
              }}
            >
              <AddIcon sx={{ mr: 1, fontSize: 20 }} />
              Create new item
            </MenuItem>
            {organizationChecklistItems
              ?.slice()
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((item) => (
                <MenuItem
                  key={item.id}
                  onClick={() => handleAddChecklistItem(item)}
                  disabled={fields.some((field) => field.checklistItemId === item.id)}
                >
                  {item.name}
                </MenuItem>
              ))}
            {organizationChecklistItems?.length === 0 && (
              <MenuItem disabled>No checklist items available</MenuItem>
            )}
          </Menu>
        </MDBox>

        <MDBox>
          {fields.length > 0 && (
            <MDBox mb={3}>
              <Controller
                control={control}
                name="checklistItems"
                render={({ field }) => (
                  <>
                    {fields.map((item, index) => (
                      <FormGroup key={item.id}>
                        <MDBox display="flex" alignItems="center" justifyContent="space-between">
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={field.value[index]?.isCompleted || false}
                                onChange={(e) => {
                                  const newItems = [...field.value];
                                  newItems[index] = {
                                    ...newItems[index],
                                    isCompleted: e.target.checked,
                                  };
                                  field.onChange(newItems);
                                }}
                                name={item.name}
                              />
                            }
                            label={
                              <MDTypography
                                variant="body2"
                                sx={{
                                  textDecoration: field.value[index]?.isCompleted
                                    ? "line-through"
                                    : "none",
                                  color: field.value[index]?.isCompleted
                                    ? "text.secondary"
                                    : "text.primary",
                                }}
                              >
                                {item.name}
                              </MDTypography>
                            }
                          />
                          <IconButton
                            size="small"
                            onClick={() => remove(index)}
                            sx={{
                              opacity: 0.6,
                              "&:hover": {
                                opacity: 1,
                              },
                            }}
                          >
                            <Close fontSize="small" />
                          </IconButton>
                        </MDBox>
                      </FormGroup>
                    ))}
                  </>
                )}
              />
            </MDBox>
          )}

          <MDBox>
            <MDTypography variant="subtitle2" fontWeight="medium" mb={1}>
              Notes:
            </MDTypography>
            <Controller
              name="checklistNotes"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  multiline
                  rows={4}
                  fullWidth
                  placeholder="Enter checklist notes here..."
                  sx={{
                    bgcolor: "white",
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 1,
                      bgcolor: "white",
                    },
                  }}
                />
              )}
            />
            <MDBox display="flex" justifyContent="flex-end" mt={2}>
              <MDButton
                variant="contained"
                color="info"
                size="small"
                onClick={handleSubmit(onSubmit)}
                disabled={isSubmitting || !isDirty}
              >
                {isSubmitting ? "Saving..." : "Save Changes"}
              </MDButton>
            </MDBox>
          </MDBox>
        </MDBox>
      </MDBox>

      <CreateChecklistItemModal
        open={createModalOpen}
        onClose={handleCreateModalClose}
        onSuccess={handleCreateSuccess}
      />
    </>
  );
};

export default JobPhaseChecklist;
